import _defineProperty from "/opt/build/repo/website/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "/opt/build/repo/website/node_modules/@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { useId } from '@reach/auto-id';
import classnames from 'classnames2';
import React, { Children, cloneElement, useEffect, useRef, useState } from 'react';
import { Popper } from '../Popper';
import styles from './styles.scss';

var wrapEvent = function wrapEvent(child, theirHandler, ourHandler) {
  return function (event) {
    if (typeof child !== 'string' && child.props[theirHandler]) {
      child.props[theirHandler](event);
    }

    if (!event.defaultPrevented) {
      return ourHandler(event);
    }
  };
};

export var Tooltip = function Tooltip(_ref) {
  var className = _ref.className,
      content = _ref.content,
      ariaLabel = _ref.ariaLabel,
      _ref$showDelay = _ref.showDelay,
      showDelay = _ref$showDelay === void 0 ? 0 : _ref$showDelay,
      _ref$hideDelay = _ref.hideDelay,
      hideDelay = _ref$hideDelay === void 0 ? 0 : _ref$hideDelay,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'top' : _ref$placement,
      children = _ref.children,
      shouldWrapChildren = _ref.shouldWrapChildren,
      dataTest = _ref.dataTest,
      onOpenProp = _ref.onOpen,
      onCloseProp = _ref.onClose;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];

  var tooltipId = "tooltip-".concat(useId());
  var referenceRef = useRef();
  var enterTimeout = null;
  var exitTimeout = null;

  var onOpen = function onOpen() {
    return setIsOpen(true);
  };

  var onClose = function onClose() {
    return setIsOpen(false);
  };

  var openWithDelay = function openWithDelay() {
    enterTimeout = setTimeout(onOpen, showDelay);
  };

  var closeWithDelay = function closeWithDelay() {
    clearTimeout(enterTimeout);
    exitTimeout = setTimeout(onClose, hideDelay);
  };

  var handleOpen = function handleOpen() {
    openWithDelay();

    if (onOpenProp) {
      onOpenProp();
    }
  };

  var handleClose = function handleClose() {
    closeWithDelay();

    if (onCloseProp) {
      onCloseProp();
    }
  };

  var handleMouseEnter = function handleMouseEnter() {
    clearTimeout(exitTimeout);
  };

  var handleMouseLeave = function handleMouseLeave() {
    setTimeout(onClose, 0);
  };

  var onKeyDown = function onKeyDown(event) {
    if (event.keyCode === 27) {
      handleClose();
    }
  };

  useEffect(function () {
    if (!isOpen) return;
    window.addEventListener('keydown', onKeyDown);
    return function () {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  var referenceProps = _objectSpread({
    ref: referenceRef,
    onMouseEnter: wrapEvent(children, 'onMouseEnter', handleOpen),
    onMouseLeave: wrapEvent(children, 'onMouseLeave', handleClose),
    onFocus: wrapEvent(children, 'onFocus', handleOpen),
    onBlur: wrapEvent(children, 'onBlur', handleClose)
  }, isOpen && {
    'aria-describedby': tooltipId
  });

  var clone;

  if (typeof children === 'string' || shouldWrapChildren) {
    clone = React.createElement("span", Object.assign({
      tabIndex: 0
    }, referenceProps), children);
  } else {
    clone = cloneElement(Children.only(children), referenceProps);
  }

  var hasAriaLabel = ariaLabel != null;
  return React.createElement(React.Fragment, null, clone, React.createElement(Popper, {
    className: classnames(styles.popper, className),
    usePortal: true,
    isOpen: isOpen,
    placement: placement,
    anchorEl: referenceRef.current,
    id: hasAriaLabel ? undefined : tooltipId,
    role: hasAriaLabel ? undefined : 'tooltip',
    dataTest: dataTest,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave
  }, React.createElement("div", {
    className: styles.tooltip
  }, content), hasAriaLabel && React.createElement("div", {
    role: "tooltip",
    id: tooltipId,
    className: styles.visuallyHidden
  }, ariaLabel)));
};